// Warning: Early load!
// Do not use tm:* imports on root level.

import { getModuleLoader } from "@/platform/_preload";
import Spinner from "@/platform/spinner";

type Runner = (...args: any[]) => /* unmap */ (() => void);
type AsyncRunner = (...args: any[]) => Promise<(() => void)|void>;


export function getRunner(moduleName: string|Runner|AsyncRunner): AsyncRunner {
    return async (...args: any[]) => {
        let module = await Spinner.run(async () => {
            if (typeof moduleName === "string") {
                const loader = getModuleLoader();
                await loader.when_declared(moduleName);
                return await loader.require(moduleName);
            } else {
                return moduleName as any;
            }
        });

        let func!: (...args: any[]) => Promise<() => void>|void;
        if (!!(module as any)["runSimple"]) {
            func = (module as any)["runSimple"] as any;
        } else if (!!(module as any)["default"]) {
            func = (module as any)["default"] as any;
        } else {
            func = module as any;
        }
        return Spinner.around(Promise.resolve().then(() => func(...args) as any));
    }
}

export function rawJSRunner(child: Runner|AsyncRunner): AsyncRunner {
    return async (text: string) => {
        const {scriptEval} = await import("@/utils/scripts");
        const args = await scriptEval<any[]>("return " + text + ";");
        return await child(...args);
    }
}

function parseEntrypointArgs(parent: Runner|AsyncRunner): AsyncRunner {
    return async (self: Entrypoint) => {
        let current = parent;
        let args: any[] = [];
        if ((self.getAttribute("format") || "json") === "json") {
            if (self.getAttribute("mount") !== "no")
                args.push(self);

            const rawConfig = JSON.parse(self.getAttribute("config") || "null");
            if (Array.isArray(rawConfig)) {
                args = [...args, ...rawConfig];
            } else {
                args.push(rawConfig);
            }
        } else if (self.getAttribute("format") === "raw") {
            args.push(self.getAttribute("config") || "[]")
            current = rawJSRunner(current);
        }
        return await current(...args);
    }
}


// The reporter must be looked up at throw time, not captured here: _error.ts
// replaces self.reportError with the interceptor bus (which is what forwards to
// Sentry), and it loads at boot stage 3 - after this module is required at stage
// 2. Capturing the reference in the default argument pinned the *native*
// reportError, so entrypoint failures only ever reached the console.
export function deasync(parent: AsyncRunner|Runner, report: (e: unknown) => void = (e) => window.reportError(e)): Runner {
    return (...args: any[]) => {
        let connected = true;
        let unloader = () => {};

        (async () => {
            const result = await parent(...args);
            if (!!result) {
                if (!connected) {
                    result();
                    return;
                }
                unloader = result;
            }
        })().catch(report);

        return () => {
            connected = false;
            unloader();
            unloader = () => {};
        }
    }
}

const _css = new CSSStyleSheet();
_css.insertRule("tmhtml-entrypoint { display: contents; }");
document.adoptedStyleSheets.push(_css);



class _EntrypointHanndlerImpls extends HTMLElement {
    public renderSlot(target: HTMLElement, slot="default"): () => void {throw new Error("Not implemented");}
}


const _ehm = new WeakMap<Window, {new (..._: any[]): HTMLElement}>();
function EntrypointHandlerMaker(win: Window&typeof globalThis = window): typeof _EntrypointHanndlerImpls {
    if (!_ehm.has(win)) {
        _ehm.set(win, class extends (win.HTMLElement as typeof HTMLElement) {
            
            #unload: () => void = () => {};
            #connected = false;
            #read = false;
        
            #prior: Node[] = [];
            #slots: Map<string, Node[]> = new Map();
            #rendered: Map<string, () => void> = new Map();
        
            protected _getNewRunner(): (handler: HTMLElement) => () => void {
                throw new Error("Not implemented error");
            }
        
            #__recoverSlots() {
                this.#slots.clear();
                this.#prior.forEach(n => this.append(n));
                this.#unload = () => {};
            }
        
            #__readSlots() {
                if (!this.#read) {
                    this.#read = true;
                    this.#prior = Array.from(this.childNodes);
                    for (let node of this.#prior) {
                        let slot = "default";
                        if (node instanceof Element && !!node.getAttribute("data-slot")) {
                            slot = node.getAttribute("data-slot")!;
                        }
                        const list = this.#slots.get(slot)||[];
                        list.push(node);
                        this.#slots.set(slot, list);
                    }
                }
                this.#prior.forEach(n => this.removeChild(n));
            }
        
            #__unload() {
                this.#unload();
                this.#unload = () => {this.#__recoverSlots()};
            }
        
            public renderSlot(target: HTMLElement, slot="default") {
                if (this.#rendered.has(slot)) throw new Error("Double render of slot.");
                if (!this.#slots.has(slot)) return () => {};
        
                const nodes = this.#slots.get(slot)!;
                target.append(...nodes);
        
                let unload = () => {
                    nodes.forEach(n => target.removeChild(n));
                    this.#rendered.delete(slot);
                    unload = () => {};
                }
        
                this.#rendered.set(slot, () => unload());
                return () => unload();
            }
        
            __reload() {
                if (!this.#connected) return;
        
                this.#__unload();
                this.#__readSlots();
                this.#unload = this._getNewRunner()(this);
            }
            
            connectedCallback() {
                this.#__unload();
                this.#connected = true;
                this.__reload();
            }
        
             disconnectedCallback() {
                 this.#__unload();
                 this.#connected = false;
             }
        
             attributeChangedCallback() {
                 this.__reload();
             }
        })
    }
    return _ehm.get(win)! as any;

}

export const EntrypointHandler = EntrypointHandlerMaker();
export type EntrypointHandler = _EntrypointHanndlerImpls;

export default class Entrypoint extends EntrypointHandler {
   static observedAttributes = ["module", "config", "mount", "format"];

   // A directly-assigned runner function. Takes precedence over the `module`
   // attribute, so callers holding a function can mount it without registering
   // a dedicated element via registerHardElement.
   #moduleFn?: Runner|AsyncRunner;

   get module(): string|Runner|AsyncRunner|null {
       return this.#moduleFn ?? this.getAttribute("module");
   }

   set module(value: string|Runner|AsyncRunner|null) {
       if (typeof value === "function") {
           this.#moduleFn = value;
           // No attribute change to trigger attributeChangedCallback, so reload here.
           (this as any).__reload();
           return;
       }

       this.#moduleFn = undefined;
       if (value === null) {
           // removeAttribute of an absent attribute is a genuine no-op (fires
           // no attributeChangedCallback), so reload explicitly in that case —
           // e.g. clearing a previously-assigned function on an attribute-less
           // element. When the attribute is present, removing it drives the
           // single reload via attributeChangedCallback.
           if (this.hasAttribute("module")) this.removeAttribute("module");
           else (this as any).__reload();
       } else {
           // setAttribute always fires attributeChangedCallback (even when the
           // value is unchanged), which drives exactly one reload.
           this.setAttribute("module", value);
       }
   }

   protected _getNewRunner(): (self: HTMLElement) => () => void {
       let newRunner = getRunner(this.#moduleFn ?? (this.getAttribute("module") || "_doesNotExist"));
       newRunner = parseEntrypointArgs(newRunner);
       return (self) => (deasync(newRunner))(self);
   }
}
customElements.define("tmhtml-entrypoint", Entrypoint);



// interface DirectRunnerLifetime<T extends string> {
//     close: () => void;
//     onAttributeChange: (cfg: Partial<Record<T, string|null>>) => void;
// 
// }
// type DirectRunner<T extends string> = (element: HTMLElement, cfg: Record<T, string|null>) => DirectRunnerLifetime<T>;
// const nullLifetime: DirectRunnerLifetime<any> = {close(){}, onAttributeChange()};
// export function registerDirectElement<T extends string>(direct: DirectRunner<T>, name: string, attributes: T[], win: Window&(typeof globalThis) = window) {
//     if (!_cssWeakRef.has(win.document)) {
//         const css: CSSStyleSheet = new (win as any).CSSStyleSheet();
//         _cssWeakRef.set(win.document, css);
//         win.document.adoptedStyleSheets.push(css);
//     }
//     const css = _cssWeakRef.get(win.document)!;
//     css.insertRule(`${name} { display: contents; }`);
// 
//     const handler = (class extends win.HTMLElement {
//         static observedAttributes = attributes;
//         #lifetime: DirectRunnerLifetime<T> = nullLifetime;
// 
//         #unload() {
//             this.#lifetime.close();
//             this.#lifetime = nullLifetime;
//         }
// 
//         connectedCallback() {
//             this.#unload();
//             const attrs: Record<T, string|null> = {} as any;
//             for (let attr of attributes) {
//                 attrs[attr] = this.getAttribute(attr);
//             }
//             this.#lifetime = direct(this, attrs);
//         }
//         
//         disconnectedCallback() {
//             this.#unload();
//         }
//         
//         attributeChangedCallback(name: T, _, newValue: string|null) {
//             this.#lifetime.onAttributeChange({[name]: newValue});
//         }
// 
//     });
//     win.customElements.define(name, handler);
// }



const _cssWeakRef = new WeakMap<Document, CSSStyleSheet>();
export function registerHardElement(runner: Runner|AsyncRunner, name: string, win: Window&(typeof globalThis) = window) {
    if (!_cssWeakRef.has(win.document)) {
        const css: CSSStyleSheet = new (win as any).CSSStyleSheet();
        _cssWeakRef.set(win.document, css);
        win.document.adoptedStyleSheets.push(css);
    }
    const css = _cssWeakRef.get(win.document)!;
    css.insertRule(`${name} { display: contents; }`);
    
    const handler = (class extends EntrypointHandlerMaker(win) {
       static observedAttributes = [];

       protected _getNewRunner(): (self: HTMLElement) => () => void {
           return deasync(runner);
       }
    });
    win.customElements.define(name, handler);
}
